<template>
  <div class="spine-science-page" v-loading="loading">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/menu/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>内容管理</el-breadcrumb-item>
      <el-breadcrumb-item>脊柱侧弯科普</el-breadcrumb-item>
    </el-breadcrumb>

    <el-row :gutter="16" class="content-row">
      <el-col :span="5">
        <el-card class="category-card">
          <div slot="header" class="card-header">
            <span>科普分类</span>
            <div>
              <el-button type="text" size="small" @click="openCategoryDialog()">新增</el-button>
              <el-button type="text" size="small" :disabled="!currentCategory" @click="openCategoryDialog(currentCategory)">设置</el-button>
            </div>
          </div>
          <div
            v-for="category in categories"
            :key="category.id"
            class="category-item"
            :class="{ active: category.id === selectedCategoryId }"
            @click="selectCategory(category)"
          >
            <span>{{ category.name }}</span>
            <el-tag size="mini" :type="category.active ? 'success' : 'info'">{{ category.active ? '启用' : '停用' }}</el-tag>
          </div>
          <div v-if="categories.length === 0" class="empty-category">暂无分类</div>
        </el-card>
      </el-col>

      <el-col :span="19">
        <el-card>
          <div slot="header" class="card-header">
            <span>{{ currentCategory ? currentCategory.name : '请选择分类' }}</span>
            <el-button type="primary" size="small" :disabled="!currentCategory" @click="openArticleDialog()">新增文章</el-button>
          </div>

          <el-table :data="articles" border size="small" empty-text="该分类暂无文章">
            <el-table-column prop="title" label="标题" min-width="180" />
            <el-table-column prop="summary" label="摘要" min-width="260" show-overflow-tooltip />
            <el-table-column prop="sort" label="排序" width="80" align="center" />
            <el-table-column label="状态" width="90" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.active ? 'success' : 'info'" size="mini">{{ scope.row.active ? '已发布' : '未发布' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="更新时间" width="170">
              <template slot-scope="scope">{{ formatDate(scope.row.gmtModified) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="180" align="center">
              <template slot-scope="scope">
                <el-button type="text" size="small" @click="openArticleDialog(scope.row)">编辑</el-button>
                <el-button type="text" size="small" @click="toggleArticle(scope.row)">{{ scope.row.active ? '下架' : '发布' }}</el-button>
                <el-button type="text" size="small" class="danger-button" @click="removeArticle(scope.row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-dialog :title="categoryForm && categoryForm.id ? '分类设置' : '新增分类'" :visible.sync="categoryDialogVisible" width="520px" :close-on-click-modal="false">
      <el-form v-if="categoryForm" label-width="92px">
        <el-form-item label="分类名称"><el-input v-model="categoryForm.name" /></el-form-item>
        <el-form-item label="排序"><el-input-number v-model="categoryForm.sort" :min="0" /></el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="categoryForm.active">
            <el-radio :label="true">启用</el-radio>
            <el-radio :label="false">停用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button v-if="categoryForm && categoryForm.id" type="danger" plain @click="removeCategory">删除分类</el-button>
        <el-button @click="categoryDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveCategory">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="articleForm.id ? '编辑科普文章' : '新增科普文章'" :visible.sync="articleDialogVisible" width="980px" top="5vh" :close-on-click-modal="false">
      <el-form label-width="82px">
        <el-row :gutter="16">
          <el-col :span="16"><el-form-item label="文章标题"><el-input v-model="articleForm.title" maxlength="100" show-word-limit /></el-form-item></el-col>
          <el-col :span="8"><el-form-item label="排序"><el-input-number v-model="articleForm.sort" :min="0" /></el-form-item></el-col>
        </el-row>
        <el-form-item label="文章封面">
          <div class="cover-upload-row">
            <FileUploadOssPics
              :file-list="coverFileList"
              :file-limit="1"
              dir="spine/science/cover"
              @change="onCoverFileChange"
              @urlListChange="onCoverUrlChange"
            />
            <div class="cover-upload-hint">支持上传单张封面图；上传成功后会自动保存图片地址。</div>
          </div>
          <el-input v-model="articleForm.coverUrl" placeholder="也可直接填写图片 URL" class="cover-url-input" />
        </el-form-item>
        <el-form-item label="文章摘要"><el-input v-model="articleForm.summary" type="textarea" :rows="3" maxlength="500" show-word-limit /></el-form-item>
        <el-form-item label="发布状态">
          <el-radio-group v-model="articleForm.active">
            <el-radio :label="true">发布</el-radio>
            <el-radio :label="false">暂存</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="富文本正文" required>
          <quill-editor
            ref="articleEditor"
            v-model="articleForm.content"
            :options="editorOption"
            class="article-editor"
            @ready="onEditorReady($event)"
          />
          <div class="editor-upload-hint">
            支持通过工具栏的视频按钮上传 MP4、WebM、Ogg 格式视频（单个不超过 200MB），移动端可直接播放。
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="articleDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveArticle">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { Quill, quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import ImageResize from 'quill-image-resize-module'
import { uploadFile } from '@/utils/AliOssUploadUtils'
import FileUploadOssPics from '@/common-components/FileUploadOss/FileUploadOssPics.vue'

Quill.register('modules/ImageResize', ImageResize)

const BlockEmbed = Quill.import('blots/block/embed')
class SpineVideoBlot extends BlockEmbed {
  static create (value) {
    const node = super.create()
    node.setAttribute('src', value)
    node.setAttribute('controls', 'controls')
    node.setAttribute('preload', 'metadata')
    node.setAttribute('playsinline', 'playsinline')
    node.setAttribute('webkit-playsinline', 'webkit-playsinline')
    node.classList.add('spine-science-video')
    return node
  }

  static value (node) {
    return node.getAttribute('src')
  }
}
SpineVideoBlot.blotName = 'spineVideo'
SpineVideoBlot.tagName = 'video'
Quill.register(SpineVideoBlot)

const emptyArticle = () => ({
  id: null,
  title: '',
  summary: '',
  coverUrl: '',
  content: '',
  sort: 0,
  active: true
})

const emptyCategory = sort => ({
  id: null,
  name: '',
  sort,
  active: true
})

export default {
  name: 'SpineScience',
  components: { quillEditor, FileUploadOssPics },
  data () {
    return {
      loading: false,
      categories: [],
      articles: [],
      selectedCategoryId: null,
      currentCategory: null,
      categoryDialogVisible: false,
      articleDialogVisible: false,
      categoryForm: null,
      articleForm: emptyArticle(),
      coverFileList: [],
      videoUploading: false,
      articleEditorListeners: null,
      editorOption: {
        theme: 'snow',
        modules: {
          ImageResize: {
            modules: ['Resize', 'DisplaySize', 'Toolbar']
          },
          toolbar: {
            container: [
              [{ header: [2, 3, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ color: [] }, { background: [] }],
              [{ align: [] }],
              ['link', 'image', 'spineVideo', 'clean']
            ],
            handlers: {
              image: () => {
                this.selectImageToUpload()
              },
              spineVideo: () => {
                this.uploadVideoToEditor()
              }
            }
          }
        }
      }
    }
  },
  mounted () {
    this.loadCategories()
    this.$nextTick(() => this.initVideoToolbar())
  },
  methods: {
    initVideoToolbar () {
      const videoButton = this.$el.querySelector('.article-editor .ql-toolbar .ql-spineVideo')
      if (videoButton) {
        videoButton.setAttribute('title', '上传视频')
        videoButton.setAttribute('aria-label', '上传视频')
      }
    },
    async loadCategories () {
      this.loading = true
      try {
        const { data: resp } = await this.$http.get('/cms/spine/science/category/list')
        if (resp.code !== 200) throw new Error(resp.message)
        this.categories = resp.data || []
        const selected = this.categories.find(item => item.id === this.selectedCategoryId) || this.categories[0]
        if (selected) {
          this.selectCategory(selected)
        } else {
          this.currentCategory = null
          this.selectedCategoryId = null
          this.articles = []
        }
      } catch (e) {
        this.$message.error(e.message || '加载分类失败')
      } finally {
        this.loading = false
      }
    },
    async selectCategory (category) {
      this.currentCategory = category
      this.selectedCategoryId = category.id
      await this.loadArticles()
    },
    async loadArticles () {
      if (!this.currentCategory) return
      this.loading = true
      try {
        const { data: resp } = await this.$http.get('/cms/spine/science/article/list', { params: { categoryId: this.currentCategory.id } })
        if (resp.code !== 200) throw new Error(resp.message)
        this.articles = resp.data || []
      } catch (e) {
        this.$message.error(e.message || '加载文章失败')
      } finally {
        this.loading = false
      }
    },
    openCategoryDialog (category) {
      const nextSort = this.categories.reduce((max, item) => Math.max(max, item.sort || 0), 0) + 10
      this.categoryForm = category ? Object.assign({}, category) : emptyCategory(nextSort)
      this.categoryDialogVisible = true
    },
    async saveCategory () {
      try {
        const { data: resp } = await this.$http.post('/cms/spine/science/category/save', this.categoryForm)
        if (resp.code !== 200) throw new Error(resp.message)
        this.categoryDialogVisible = false
        this.$message.success('分类已保存')
        await this.loadCategories()
      } catch (e) {
        this.$message.error(e.message || '保存分类失败')
      }
    },
    async removeCategory () {
      try {
        await this.$confirm(`确定删除分类“${this.categoryForm.name}”吗？分类下不能存在文章。`, '删除分类', { type: 'warning' })
        const { data: resp } = await this.$http.delete('/cms/spine/science/category/delete', { params: { id: this.categoryForm.id } })
        if (resp.code !== 200) throw new Error(resp.message)
        this.categoryDialogVisible = false
        this.selectedCategoryId = null
        this.currentCategory = null
        this.$message.success('分类已删除')
        await this.loadCategories()
      } catch (e) {
        if (e !== 'cancel') this.$message.error(e.message || '删除分类失败')
      }
    },
    openArticleDialog (article) {
      this.articleForm = Object.assign(emptyArticle(), article || {})
      this.coverFileList = this.articleForm.coverUrl
        ? [{ name: '文章封面', url: this.articleForm.coverUrl }]
        : []
      this.articleDialogVisible = true
    },
    onCoverFileChange (fileList) {
      this.coverFileList = fileList
      if (fileList.length === 0) this.articleForm.coverUrl = ''
    },
    onCoverUrlChange (urlList) {
      this.articleForm.coverUrl = urlList[0] || ''
    },
    onEditorReady (quill) {
      const handlePaste = async event => {
        const clipboardData = event.clipboardData
        const items = Array.from((clipboardData && clipboardData.items) || [])
        const imageItem = items.find(item => item.kind === 'file' && /^image\//i.test(item.type))
        const html = clipboardData && clipboardData.getData('text/html')

        if (imageItem) {
          const file = imageItem.getAsFile()
          if (!file) return

          event.preventDefault()
          event.stopPropagation()
          this.uploadImageToEditor(file, quill)
          return
        }

        if (!html || !/<img[\s>]/i.test(html)) return

        event.preventDefault()
        event.stopPropagation()
        const range = quill.getSelection(true) || { index: quill.getLength() }
        try {
          const normalizedHtml = await this.uploadClipboardImages(html)
          quill.clipboard.dangerouslyPasteHTML(range.index, normalizedHtml, 'user')
        } catch (error) {
          console.error('网页图片上传失败', error)
          this.$message.error('请先保存第三方图片再粘贴进来')
        }
      }
      const handleDrop = event => {
        const file = Array.from((event.dataTransfer && event.dataTransfer.files) || [])
          .find(item => /^image\//i.test(item.type))
        if (!file) return

        event.preventDefault()
        event.stopPropagation()
        this.uploadImageToEditor(file, quill)
      }

      quill.root.addEventListener('paste', handlePaste)
      quill.root.addEventListener('drop', handleDrop)
      this.articleEditorListeners = { quill, handlePaste, handleDrop }
    },
    async uploadClipboardImages (html) {
      const container = document.createElement('div')
      container.innerHTML = html
      const images = Array.from(container.querySelectorAll('img'))

      await Promise.all(images.map(async image => {
        const source = image.getAttribute('src')
        if (!source || !/^https?:\/\//i.test(source) || this.isOssImageUrl(source)) return

        const response = await fetch(source, { mode: 'cors' })
        if (!response.ok) throw new Error(`图片下载失败: ${response.status}`)
        const blob = await response.blob()
        const file = new File([blob], this.getClipboardImageName(source, blob.type), {
          type: blob.type || 'image/png'
        })
        const fileUrl = await this.uploadImageFile(file)
        image.setAttribute('src', fileUrl)
      }))

      return container.innerHTML
    },
    isOssImageUrl (url) {
      return /^https:\/\/ihealthy\.oss-cn-shenzhen\.aliyuncs\.com\//i.test(url)
    },
    getClipboardImageName (source, contentType) {
      const pathname = new URL(source).pathname
      const sourceName = pathname.substring(pathname.lastIndexOf('/') + 1)
      if (sourceName && /\.[a-z0-9]+$/i.test(sourceName)) return sourceName
      const extension = (contentType.split('/')[1] || 'png').split('+')[0]
      return `clipboard-image-${Date.now()}.${extension}`
    },
    uploadImageFile (file) {
      return new Promise((resolve, reject) => {
        uploadFile(
          'spine/science/article',
          file,
          () => {},
          resolve,
          reject
        )
      })
    },
    selectImageToUpload () {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.onchange = event => {
        const file = event.target.files && event.target.files[0]
        if (file) this.uploadImageToEditor(file)
      }
      input.click()
    },
    uploadImageToEditor (file, quill = this.$refs.articleEditor && this.$refs.articleEditor.quill) {
      if (!quill) {
        this.$message.error('未找到富文本编辑器')
        return
      }

      const range = quill.getSelection(true) || { index: quill.getLength() }
      this.uploadImageFile(file)
        .then(fileUrl => {
          const insertIndex = Math.min(range.index, quill.getLength())
          quill.insertEmbed(insertIndex, 'image', fileUrl, 'user')
          quill.setSelection(insertIndex + 1, 0, 'silent')
        })
        .catch(() => this.$message.error('正文图片上传失败'))
    },
    uploadVideoToEditor () {
      if (this.videoUploading) return
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'video/mp4,video/webm,video/ogg'
      input.onchange = async event => {
        const file = event.target.files && event.target.files[0]
        if (!file) return

        const supportedTypes = ['video/mp4', 'video/webm', 'video/ogg']
        const fileExtension = (file.name.split('.').pop() || '').toLowerCase()
        if (!supportedTypes.includes(file.type) && !['mp4', 'webm', 'ogv', 'ogg'].includes(fileExtension)) {
          this.$message.warning('仅支持 MP4、WebM、Ogg 格式的视频')
          return
        }
        if (file.size > 200 * 1024 * 1024) {
          this.$message.warning('视频大小不能超过 200MB')
          return
        }

        this.videoUploading = true
        const loading = this.$loading({ text: '视频上传中，请勿关闭页面…' })
        uploadFile(
          'spine/science/video',
          file,
          () => {},
          fileUrl => {
            const quill = this.$refs.articleEditor && this.$refs.articleEditor.quill
            if (!quill) {
              this.$message.error('未找到富文本编辑器')
              return
            }
            const range = quill.getSelection(true)
            quill.insertEmbed(range.index, 'spineVideo', fileUrl, 'user')
            quill.setSelection(range.index + 1, 0, 'silent')
            this.$message.success('视频已插入正文')
          },
          () => this.$message.error('正文视频上传失败')
        ).finally(() => {
          this.videoUploading = false
          loading.close()
        })
      }
      input.click()
    },
    async saveArticle () {
      if (!this.articleForm.title.trim() || !this.articleForm.content.trim()) {
        this.$message.warning('请填写文章标题和富文本正文')
        return
      }
      try {
        const payload = Object.assign({}, this.articleForm, { categoryId: this.currentCategory.id })
        const { data: resp } = await this.$http.post('/cms/spine/science/article/save', payload)
        if (resp.code !== 200) throw new Error(resp.message)
        this.articleDialogVisible = false
        this.$message.success('文章已保存')
        await this.loadArticles()
      } catch (e) {
        this.$message.error(e.message || '保存文章失败')
      }
    },
    async toggleArticle (article) {
      try {
        const { data: resp } = await this.$http.post('/cms/spine/science/article/active', null, {
          params: { id: article.id, active: !article.active }
        })
        if (resp.code !== 200) throw new Error(resp.message)
        await this.loadArticles()
      } catch (e) {
        this.$message.error(e.message || '更新文章状态失败')
      }
    },
    async removeArticle (article) {
      try {
        await this.$confirm(`确定删除“${article.title}”吗？`, '删除文章', { type: 'warning' })
        const { data: resp } = await this.$http.delete('/cms/spine/science/article/delete', { params: { id: article.id } })
        if (resp.code !== 200) throw new Error(resp.message)
        this.$message.success('文章已删除')
        await this.loadArticles()
      } catch (e) {
        if (e !== 'cancel') this.$message.error(e.message || '删除文章失败')
      }
    },
    formatDate (value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    }
  },
  beforeDestroy () {
    const listeners = this.articleEditorListeners
    if (!listeners) return
    listeners.quill.root.removeEventListener('paste', listeners.handlePaste)
    listeners.quill.root.removeEventListener('drop', listeners.handleDrop)
  }
}
</script>

<style scoped>
.spine-science-page { padding: 16px; }
.content-row { margin-top: 16px; }
.card-header { display: flex; align-items: center; justify-content: space-between; }
.category-item { display: flex; align-items: center; justify-content: space-between; padding: 12px 10px; border-bottom: 1px solid #ebeef5; cursor: pointer; }
.category-item.active { color: #409EFF; background: #ecf5ff; font-weight: 600; }
.empty-category { padding: 18px 0; text-align: center; color: #909399; }
.article-editor { min-height: 300px; }
.article-editor :deep(.ql-toolbar .ql-spineVideo) { width: 28px; }
.article-editor :deep(.ql-toolbar .ql-spineVideo::before) {
  content: '▶';
  display: block;
  color: #444;
  font-size: 18px;
  font-style: normal;
  line-height: 18px;
  text-align: center;
}
.article-editor :deep(.ql-toolbar .ql-spineVideo:hover::before) { color: #06c; }
.editor-upload-hint { margin-top: 8px; color: #909399; font-size: 13px; line-height: 1.5; }
.cover-upload-row { display: flex; align-items: center; gap: 16px; }
.cover-upload-hint { color: #909399; font-size: 13px; }
.cover-url-input { margin-top: 10px; }
.danger-button { color: #f56c6c; }
</style>
